import { debug } from '@/config'
import Vue from 'vue'
import Vuex, {
  ActionTree,
  Commit,
  createLogger,
  GetterTree,
  MutationTree,
  StoreOptions,
} from 'vuex'
import { vuexfireMutations } from 'vuexfire'
import admin from './modules/admin'
import config from './modules/config'
import drive from './modules/drive'
import event from './modules/events'
import file from './modules/file'
import link from './modules/link'
import notification from './modules/notification'
import payment from './modules/payment'
import sharedFile from './modules/sharedFile'
import team from './modules/team'
import transcoding from './modules/transcoding'
import user from './modules/user'
import workstation from './modules/workstation'
import { RequestInfo, RequestName, RequestStatus, RequestUpdate, RootState } from './types/root'

Vue.use(Vuex)

/**
 * A helper for overwriting given request status and commiting notification changes
 *
 * @param commit from vuex context
 * @param name name of a request
 * @param status the currrent status
 * @param notificationContent the text of notification
 */
export const updateRequest = (
  commit: Commit,
  name: RequestName,
  status: RequestStatus,
  notificationContent?: string,
) => {
  const error = status === 'failed' ? notificationContent : undefined
  commit('SET_REQUEST_INFO', new RequestUpdate(name, status, error), { root: true })
  if (notificationContent) {
    if (status === 'successful' || status === 'pending') {
      commit('notification/ADD_SUCCESS_NOTIFICATION', notificationContent, { root: true })
    } else if (status === 'failed') {
      commit('notification/ADD_ERROR_NOTIFICATION', notificationContent, { root: true })
    }
  }
}

type RequestHelperOptions = {
  pendingNotification?: string
}

/**
 * A helper to aid us in keeping track of outgoing requests.
 * It returns two functions that use updateRequest under the hood
 * and commit new notifications.
 *
 * @param dispatch from context
 * @param name name of a request
 * @param helperOptions
 * @returns an array of two helper functions, setSuccess and setFailure
 */
export const initRequestHelper = (
  commit: Commit,
  name: RequestName,
  options?: RequestHelperOptions,
) => {
  updateRequest(commit, name, 'pending', options?.pendingNotification)
  const setSuccess = (notificationText?: string) =>
    updateRequest(commit, name, 'successful', notificationText)
  const setFailure = (error?: string) => updateRequest(commit, name, 'failed', error)
  return [setSuccess, setFailure]
}

/**
 * Checks whether the request's status is (already) pending.
 * @param request Request to be checked for pending state
 * @returns a boolean, representing whether a given Request has a pending status
 */
export const isAlreadyPending = (request: RequestInfo): boolean => {
  return request?.pending
}

const getters: GetterTree<RootState, any> = {
  theme(state: RootState) {
    return state.theme
  },
}
export const state: RootState = {
  version: '1.0.0',
  // Not only HTTP calls, can be e.g. Firebase action
  // basically anything that has pending -> successful | error states
  // we can model here and show spinners / successful / error messages here
  requests: {
    login: new RequestInfo(),
    logOut: new RequestInfo(),
    social: new RequestInfo(),
    registration: new RequestInfo(),
    profileUpdate: new RequestInfo(),
    billingUpdate: new RequestInfo(),
    paymentMethodUpdate: new RequestInfo(),
    creditDataUpdate: new RequestInfo(),
    storageTierUpdate: new RequestInfo(),
    passwordChange: new RequestInfo(),
    passwordReset: new RequestInfo(),
    createWorkstation: new RequestInfo(),
    createDrive: new RequestInfo(),
    createResources: new RequestInfo(),
    setupOnboarding: new RequestInfo(),
    getEvents: new RequestInfo(),
    getInvites: new RequestInfo(),
    getTeamMembers: new RequestInfo(),
    inviteTeamMember: new RequestInfo(),
    removeMember: new RequestInfo(),
    cancelInvite: new RequestInfo(),
    acceptInvite: new RequestInfo(),
    assignResource: new RequestInfo(),
    currentResourceMembers: new RequestInfo(),
    loadFiles: new RequestInfo(),
    linkShare: new RequestInfo(),
    linkUnshare: new RequestInfo(),
    linkList: new RequestInfo(),
    getAdminUsers: new RequestInfo(),
    bugReport: new RequestInfo(),
    contactSupport: new RequestInfo(),
    subscriptionCancel: new RequestInfo(),
    canceledSubscriptionContinue: new RequestInfo(),
    getConfig: new RequestInfo(),
    redeemCode: new RequestInfo(),
    addCredits: new RequestInfo(),
    fileTranscode: new RequestInfo(),
    listTranscodingJobs: new RequestInfo(),
    deleteTranscodingJob: new RequestInfo(),
  },
  theme: localStorage?.getItem('theme') || 'dark',
} as RootState

const actions: ActionTree<RootState, RootState> = {
  resetGlobalState({ dispatch }) {
    const resetModulesState = [
      dispatch('admin/resetAdminState'),
      dispatch('config/resetConfigState'),
      dispatch('drive/resetDriveState'),
      dispatch('event/resetEventsState'),
      dispatch('file/resetFileState'),
      dispatch('notification/resetNotificationState'),
      dispatch('workstation/resetWorkstationState'),
      dispatch('payment/resetPaymentState'),
      dispatch('team/resetTeamState'),
      dispatch('user/resetUserState'),
      dispatch('workstation/resetWorkstationState'),
      dispatch('transcoding/resetTranscodingState'),
    ]
    return Promise.all(resetModulesState)
  },
}

const mutations: MutationTree<RootState> = {
  SET_REQUEST_INFO(state, update: RequestUpdate) {
    state.requests[update.name] = update.info
  },
  setTheme(state, theme) {
    state.theme = theme
    localStorage.setItem('theme', theme)
  },
  ...vuexfireMutations,
}

const modules = {
  user,
  config,
  workstation,
  drive,
  event,
  file,
  admin,
  notification,
  payment,
  team,
  sharedFile,
  transcoding,
  link,
}

const store: StoreOptions<RootState> = {
  state,
  getters,
  actions,
  modules,
  mutations,
  strict: debug,
  plugins: debug ? [createLogger()] : [],
}

export default new Vuex.Store<RootState>(store)
